import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Switch,
  FormControlLabel,
  Typography,
  Grid,
  Chip,
  Button,
  Card,
  CardContent,
  List,
  Tab,
  Tabs,
  IconButton,
  Rating,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  Tooltip
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  CheckCircle as AvailableIcon,
  DoNotDisturb as BusyIcon,
  Cancel as OfflineIcon,
  DirectionsCar as CarIcon,
  DriveEta as LicenseIcon,
  Timer as TimerIcon,
  ContentCopy as CopyIcon,
  LocalTaxi as TaxiIcon,
  Logout as LogoutIcon,
  PauseCircle as PauseIcon,
  LockReset as PasswordIcon,
  Gavel as GavelIcon,
  GpsOff as GpsOffIcon,
  EventBusy as LicenseExpiryIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

import { getLicenseExpiryStatus } from '../../utils/licenseExpiry';
import { isDriverBlocked, driverBlockRemainingMs } from '../../utils/driverBlock';
import { canDeleteDriver, DELETE_REQUIRES_DEACTIVATION_HINT } from '../../utils/driverActive';
import { fetchDriverById, deleteDriver, logoutDriver, updateDriver, toggleDriverAvailability, resetDriverPassword, unblockDriver, updateRating, deleteRating, fetchGpsAnomalyEvents, setDriverGpsExempt } from '../../redux/slices/driversSlice';
import { isCentralOperationsRole } from '../../redux/slices/authSlice';
import { fetchRides } from '../../redux/slices/ridesSlice';
import { fetchShifts } from '../../redux/slices/shiftsSlice';
import PageHeader from '../common/PageHeader';
import Loading from '../common/Loading';
import ErrorMessage from '../common/ErrorMessage';
import DeleteConfirmDialog from '../common/DeleteConfirmDialog';
import AvatarUpload from '../common/AvatarUpload';
import { getRideStatusLabel } from '../../utils/rideStatusLabels';

// Styled Components

const DetailCard = styled(Card)(({ theme }) => ({
  height: '100%',
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[6],
  },
}));

const StatusChip = styled(Chip)(({ theme, status }) => {
  let color;

  switch (status) {
    case 'available':
      color = 'success';
      break;
    case 'busy':
      color = 'warning';
      break;
    case 'blocked':
      color = 'secondary';
      break;
    case 'offline':
    default:
      color = 'default';
      break;
  }

  return {
    color: status === 'offline' ? 'inherit' : 'white',
    backgroundColor: status === 'offline' ? undefined : theme.palette[color].main,
    '& .MuiSvgIcon-root': {
      fontSize: '1rem',
    },
  };
});

// Blockierungs-Status aus isOnline/isAvailable/Sperre ableiten (analog DriversList).
const deriveDetailStatus = (driver) => {
  if (!driver?.isOnline) return 'offline';
  if (isDriverBlocked(driver)) return 'blocked';
  if (driver.isAvailable) return 'available';
  return 'busy';
};

const detailStatusIcon = (status) => {
  switch (status) {
    case 'available': return <AvailableIcon />;
    case 'busy': return <BusyIcon />;
    case 'blocked': return <GavelIcon />;
    default: return <OfflineIcon />;
  }
};

const RideStatusChip = styled(Chip)(({ theme, status }) => {
  let color = 'default';
  
  switch (status) {
    case 'completed':
      color = 'success';
      break;
    case 'cancelled':
    case 'cancelled_by_driver':
      color = 'error';
      break;
    case 'in_progress':
      color = 'primary';
      break;
    case 'arrived':
      color = 'arrived';
      break;
    case 'accepted':
    case 'pre_accepted':
      color = 'info';
      break;
    case 'open':
    case 'dispatcher_pending':
    case 'requested':
      color = 'warning';
      break;
    default:
      color = 'default';
  }

  const backgroundColor =
    color === 'default'
      ? theme.palette.grey[500]
      : color === 'arrived'
        ? '#00897b'
        : theme.palette[color].main;

  return {
    color: 'white',
    backgroundColor,
    '& .MuiChip-label': {
      fontSize: '0.75rem',
    },
  };
});

const TabPanel = (props) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

const DriverDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const { currentDriver, isLoading, error } = useSelector(state => state.drivers);
  const { rides } = useSelector(state => state.rides);
  const { shifts } = useSelector(state => state.shifts);
  const currentUser = useSelector(state => state.auth.user);

  // Wer darf das Fahrer-Passwort zurücksetzen: zentrale Rollen für alle Fahrer,
  // operator_admin nur für Fahrer des eigenen Operators. Die verbindliche
  // Prüfung erfolgt serverseitig in der Cloud Function setDriverPassword.
  const canResetPassword = Boolean(
    currentUser && (
      isCentralOperationsRole(currentUser) ||
      (currentUser.role === 'operator_admin' &&
        currentDriver?.operatorId &&
        currentUser.operatorId === currentDriver.operatorId)
    )
  );

  // Bewertungen verwalten (bearbeiten/löschen): nur zentrale Rollen (Disponent/
  // Admin). Muss zur Firestore-Rule (canDispatchCentrally) für /ratings passen.
  const canManageRatings = Boolean(currentUser && isCentralOperationsRole(currentUser));

  const [tabValue, setTabValue] = useState(0);
  const [ratingEditOpen, setRatingEditOpen] = useState(false);
  const [ratingToEdit, setRatingToEdit] = useState(null);
  const [ratingEditValue, setRatingEditValue] = useState(0);
  const [ratingEditComment, setRatingEditComment] = useState('');
  const [ratingSubmitting, setRatingSubmitting] = useState(false);
  const [ratingDeleteOpen, setRatingDeleteOpen] = useState(false);
  const [ratingToDelete, setRatingToDelete] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [availabilityDialogOpen, setAvailabilityDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSubmitting, setPasswordSubmitting] = useState(false);
  // GPS-Auffaelligkeiten (gps_manipulation_erkennung_konzept.md)
  const [gpsEvents, setGpsEvents] = useState([]);
  const [gpsEventsLoading, setGpsEventsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  useEffect(() => {
    if (id) {
      dispatch(fetchDriverById(id));
      
      // Holen Sie auch die Fahrten und Schichten für diesen Fahrer
      dispatch(fetchRides({ driverId: id }));
      dispatch(fetchShifts({ driverId: id }));
    }
  }, [dispatch, id]);

  // Ereignisse nur nachladen, wenn der Fahrer ueberhaupt welche hat — der
  // Lebenszeit-Zaehler steht bereits auf dem Fahrerdokument. Das spart bei
  // jedem unauffaelligen Fahrer den Callable-Aufruf.
  const gpsAnomalyCount = currentDriver?.gpsAnomalienGesamt || 0;
  const gpsErkennungAktiv = useSelector(state => state.settings.global?.gpsErkennungAktiv) === true;
  const gpsExempt = currentDriver?.gpsErkennungAusgenommen === true;
  useEffect(() => {
    if (!id || !gpsAnomalyCount) {
      setGpsEvents([]);
      return undefined;
    }
    let cancelled = false;
    setGpsEventsLoading(true);
    dispatch(fetchGpsAnomalyEvents({ driverId: id, limit: 25 }))
      .unwrap()
      .then(events => { if (!cancelled) setGpsEvents(events); })
      .catch(() => { if (!cancelled) setGpsEvents([]); })
      .finally(() => { if (!cancelled) setGpsEventsLoading(false); });
    return () => { cancelled = true; };
  }, [dispatch, id, gpsAnomalyCount]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };
  
  const handleDeleteConfirm = () => {
    // Nur bei Erfolg zurueck zur Liste — sonst sieht der Disponent eine
    // vermeintlich geloeschte, in Wahrheit weiter bestehende Fahrerakte.
    dispatch(deleteDriver(id))
      .unwrap()
      .then(() => {
        navigate('/fahrer');
      })
      .catch((err) => {
        setSnackbar({
          open: true,
          message: typeof err === 'string' ? err : 'Fahrer konnte nicht gelöscht werden.',
          severity: 'error'
        });
      });
    setDeleteDialogOpen(false);
  };
  
  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };
  
  const handleLogoutClick = () => {
    setLogoutDialogOpen(true);
  };
  
  const handleLogoutConfirm = () => {
    dispatch(logoutDriver(id)).then(() => {
      dispatch(fetchDriverById(id));
    });
    setLogoutDialogOpen(false);
  };
  
  const handleLogoutCancel = () => {
    setLogoutDialogOpen(false);
  };

  const handlePasswordDialogOpen = () => {
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
    setPasswordDialogOpen(true);
  };

  const handlePasswordDialogClose = () => {
    if (passwordSubmitting) return;
    setPasswordDialogOpen(false);
  };

  const handlePasswordResetConfirm = async () => {
    if (newPassword.length < 6) {
      setPasswordError('Das Passwort muss mindestens 6 Zeichen lang sein.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Die Passwörter stimmen nicht überein.');
      return;
    }
    setPasswordError('');
    setPasswordSubmitting(true);
    try {
      await dispatch(resetDriverPassword({ driverId: id, newPassword })).unwrap();
      setPasswordDialogOpen(false);
      setSnackbar({
        open: true,
        message: 'Passwort wurde gesetzt. Bitte dem Fahrer mitteilen.',
        severity: 'success'
      });
    } catch (err) {
      setPasswordError(typeof err === 'string' ? err : 'Passwort konnte nicht gesetzt werden.');
    } finally {
      setPasswordSubmitting(false);
    }
  };

  const handleAvailabilityClick = () => {
    if (hasActiveRide) {
      setSnackbar({
        open: true,
        message: 'Fahrer hat eine laufende Fahrt. Status kann erst nach Abschluss geändert werden.',
        severity: 'warning'
      });
      return;
    }
    setAvailabilityDialogOpen(true);
  };

  const handleAvailabilityCancel = () => {
    setAvailabilityDialogOpen(false);
  };

  // Fahrer-Ablehnungssperre aufheben (fahrer_ablehnungssperre_konzept.md).
  const handleUnblock = () => {
    dispatch(unblockDriver({ driverId: id }))
      .unwrap()
      .then(() => {
        dispatch(fetchDriverById(id));
        setSnackbar({ open: true, message: 'Sperre aufgehoben', severity: 'success' });
      })
      .catch((error) => {
        setSnackbar({
          open: true,
          message: typeof error === 'string' ? error : 'Sperre konnte nicht aufgehoben werden.',
          severity: 'error'
        });
      });
  };

  const handlePauseSelect = (pauseType, pauseDuration) => {
    dispatch(toggleDriverAvailability({
      driverId: id,
      setAvailable: false,
      pauseType,
      pauseDuration
    })).then(() => {
      dispatch(fetchDriverById(id));
      setSnackbar({
        open: true,
        message: `Fahrer als besetzt markiert (${pauseType})`,
        severity: 'success'
      });
    });
    setAvailabilityDialogOpen(false);
  };

  const handleAvailableConfirm = () => {
    dispatch(toggleDriverAvailability({
      driverId: id,
      setAvailable: true
    })).then(() => {
      dispatch(fetchDriverById(id));
      setSnackbar({
        open: true,
        message: 'Fahrer als verfügbar markiert',
        severity: 'success'
      });
    });
    setAvailabilityDialogOpen(false);
  };

  const handleProfilePictureUpdate = (downloadURL) => {
    dispatch(updateDriver({
      id,
      user: {
        profile_picture: downloadURL
      }
    })).then(() => {
      setSnackbar({
        open: true,
        message: 'Profilbild erfolgreich aktualisiert',
        severity: 'success'
      });
    }).catch((error) => {
      setSnackbar({
        open: true,
        message: `Fehler beim Aktualisieren des Profilbilds: ${error.message}`,
        severity: 'error'
      });
    });
  };
  
  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Bewertungen nach Datum (neueste zuerst) sortiert
  const getSortedRatings = () => {
    const ratings = currentDriver?.ratings || [];
    const toMillis = (value) => {
      if (!value) return 0;
      if (typeof value.toMillis === 'function') return value.toMillis();
      if (value.seconds != null) return value.seconds * 1000;
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? 0 : date.getTime();
    };
    return [...ratings].sort((a, b) => toMillis(b.timestamp) - toMillis(a.timestamp));
  };

  const formatRatingDate = (value) => {
    if (!value) return '';
    const date = typeof value.toDate === 'function'
      ? value.toDate()
      : (value.seconds != null ? new Date(value.seconds * 1000) : new Date(value));
    return Number.isNaN(date.getTime())
      ? ''
      : new Intl.DateTimeFormat('de-DE', {
          day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
        }).format(date);
  };

  const handleRatingEditOpen = (rating) => {
    setRatingToEdit(rating);
    setRatingEditValue(rating.rating || 0);
    setRatingEditComment(rating.comment || '');
    setRatingEditOpen(true);
  };

  const handleRatingEditClose = () => {
    if (ratingSubmitting) return;
    setRatingEditOpen(false);
    setRatingToEdit(null);
  };

  const handleRatingEditSave = async () => {
    if (!ratingToEdit || !ratingEditValue) return;
    setRatingSubmitting(true);
    try {
      await dispatch(updateRating({
        driverId: id,
        ratingId: ratingToEdit.id,
        rating: ratingEditValue,
        comment: ratingEditComment,
      })).unwrap();
      setRatingEditOpen(false);
      setRatingToEdit(null);
      setSnackbar({ open: true, message: 'Bewertung aktualisiert.', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: `Bewertung konnte nicht gespeichert werden: ${error}`, severity: 'error' });
    } finally {
      setRatingSubmitting(false);
    }
  };

  const handleRatingDeleteOpen = (rating) => {
    setRatingToDelete(rating);
    setRatingDeleteOpen(true);
  };

  const handleRatingDeleteCancel = () => {
    setRatingDeleteOpen(false);
    setRatingToDelete(null);
  };

  const handleRatingDeleteConfirm = async () => {
    if (!ratingToDelete) return;
    try {
      await dispatch(deleteRating({ driverId: id, ratingId: ratingToDelete.id })).unwrap();
      setSnackbar({ open: true, message: 'Bewertung gelöscht.', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: `Bewertung konnte nicht gelöscht werden: ${error}`, severity: 'error' });
    } finally {
      setRatingDeleteOpen(false);
      setRatingToDelete(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Nicht angegeben';
    if (!dateString) return 'Nicht angegeben';
    
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date);
  };
  
  const formatDateTime = (dateString) => {
    if (!dateString) return 'Nicht angegeben';
    
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };
  
  const translateStatus = (status) => {
    const statusMap = {
      'available': 'Verfügbar',
      'busy': 'Besetzt',
      'blocked': 'Gesperrt',
      'offline': 'Offline',
      'requested': 'Angefragt',
      'accepted': 'Akzeptiert',
      'in_progress': 'In Bearbeitung',
      'completed': 'Abgeschlossen',
      'cancelled': 'Storniert',
      'scheduled': 'Geplant',
      'active': 'Aktiv',
      'online': 'Online'
    };
    
    return statusMap[status] || status;
  };
  
  const getDriverRides = () => {
    return rides.filter(ride => ride.driver_id === id);
  };

  const hasActiveRide = getDriverRides().some(
    ride => ['accepted', 'arrived', 'in_progress'].includes(ride.status)
  );

  const getDriverShifts = () => {
    return shifts.filter(shift => shift.driver_id === id);
  };
  
  if (isLoading) {
    return <Loading message="Fahrer wird geladen..." />;
  }
  
  if (error) {
    return (
      <ErrorMessage
        title="Fehler beim Laden des Fahrers"
        message={error}
        onRetry={() => dispatch(fetchDriverById(id))}
      />
    );
  }
  
  if (!currentDriver) {
    return (
      <ErrorMessage
        title="Fahrer nicht gefunden"
        message="Der angeforderte Fahrer konnte nicht gefunden werden."
        onRetry={() => navigate('/fahrer')}
      />
    );
  }
  
  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <PageHeader
        title="Fahrerdetails"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Fahrer', path: '/fahrer' },
          { label: currentDriver.user?.name || 'Fahrer', path: `/fahrer/${id}` }
        ]}
        actionButton={{
          label: 'Bearbeiten',
          icon: <EditIcon />,
          path: `/fahrer/${id}/bearbeiten`
        }}
      />
      
      {/* Fahrer Übersicht */}
      <Paper sx={{ p: 3, mb: 3 }}>
        {isDriverBlocked(currentDriver) && (
          <Alert
            severity="warning"
            icon={currentDriver.fahrerSperreQuelle === 'gps' ? <GpsOffIcon /> : <GavelIcon />}
            sx={{ mb: 2 }}
          >
            <strong>Fahrer gesperrt</strong>
            {currentDriver.fahrerSperreGrund ? ` — ${currentDriver.fahrerSperreGrund}` : ''}
            {'. '}Voraussichtlich wieder frei in etwa{' '}
            {Math.max(1, Math.ceil(driverBlockRemainingMs(currentDriver) / 60000))} Min.
          </Alert>
        )}
        <Grid container>
          <Grid item xs={12} md={8}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, md: 0 } }}>
              <AvatarUpload 
                src={currentDriver.user?.profile_picture || ''}
                alt={currentDriver.user?.name || 'F'}
                userId={id}
                onUploadComplete={handleProfilePictureUpdate}
                sx={{
                  width: theme => theme.spacing(15),
                  height: theme => theme.spacing(15),
                  fontSize: theme => theme.spacing(7),
                  marginRight: theme => theme.spacing(4),
                  border: theme => `3px solid ${theme.palette.primary.main}`,
                  boxShadow: theme => theme.shadows[3],
                }}
              />
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h4" component="h1">
                    {currentDriver.user?.name}
                  </Typography>
                  <StatusChip
                    label={translateStatus(deriveDetailStatus(currentDriver))}
                    status={deriveDetailStatus(currentDriver)}
                    size="medium"
                    icon={detailStatusIcon(deriveDetailStatus(currentDriver))}
                    sx={{ ml: 2 }}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Rating 
                    value={currentDriver.rating || 0} 
                    precision={0.5} 
                    readOnly 
                    size="medium"
                  />
                  <Typography variant="body1" sx={{ ml: 1 }}>
                    ({currentDriver.rating || '0'}/5, {currentDriver.total_rides || 0} Fahrten)
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Fahrernummer: {currentDriver.driverNumber || 'Nicht vergeben'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button 
                    variant="outlined" 
                    size="small" 
                    startIcon={<PhoneIcon />}
                    component="a"
                    href={`tel:${currentDriver.user?.phone}`}
                  >
                    {currentDriver.user?.phone}
                  </Button>
                  <Button 
                    variant="outlined" 
                    size="small" 
                    startIcon={<EmailIcon />}
                    component="a"
                    href={`mailto:${currentDriver.user?.email}`}
                  >
                    {currentDriver.user?.email}
                  </Button>
                </Box>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: { xs: 'flex-start', md: 'flex-end' }, mt: { xs: 2, md: 0 } }}>
              {isDriverBlocked(currentDriver) && (
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<GavelIcon />}
                  onClick={handleUnblock}
                >
                  Sperre aufheben
                </Button>
              )}
              {currentDriver.isOnline && !isDriverBlocked(currentDriver) && (
                <Button
                  variant="contained"
                  color={currentDriver.isAvailable ? 'warning' : 'success'}
                  disabled={hasActiveRide}
                  startIcon={currentDriver.isAvailable ? <BusyIcon /> : <AvailableIcon />}
                  onClick={handleAvailabilityClick}
                >
                  {currentDriver.isAvailable ? 'Besetzt setzen' : 'Verfügbar setzen'}
                </Button>
              )}
              {currentDriver.isOnline && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<LogoutIcon />}
                  onClick={handleLogoutClick}
                  sx={{ mr: 1 }}
                >
                  Fahrer abmelden
                </Button>
              )}
              {canResetPassword && (
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<PasswordIcon />}
                  onClick={handlePasswordDialogOpen}
                  sx={{ mr: 1 }}
                >
                  Passwort zurücksetzen
                </Button>
              )}
              {/* Löschen erst nach dem Deaktivieren — und weiterhin nur, wenn
                  der Fahrer offline ist. */}
              <Tooltip
                title={!canDeleteDriver(currentDriver) ? DELETE_REQUIRES_DEACTIVATION_HINT : ''}
              >
                <span>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleDeleteClick}
                    sx={{ mr: 1 }}
                    disabled={currentDriver.status !== 'offline' || !canDeleteDriver(currentDriver)}
                  >
                    Löschen
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      
      {/* GPS-Auffälligkeiten (gps_manipulation_erkennung_konzept.md).
          Wird nur gezeigt, wenn für diesen Fahrer überhaupt etwas
          protokolliert wurde. */}
      {(gpsAnomalyCount > 0 || gpsErkennungAktiv) && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <GpsOffIcon sx={{ mr: 1, color: 'warning.main' }} />
            <Typography variant="h6">GPS-Auffälligkeiten</Typography>
            {gpsAnomalyCount > 0 && (
              <Chip
                label={`${gpsAnomalyCount} insgesamt`}
                size="small"
                color="warning"
                sx={{ ml: 1 }}
              />
            )}
          </Box>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
            Protokollierte Hinweise auf manipulierte Standortdaten. Ein Eintrag ist noch kein Beweis —
            den vollständigen Verlauf mit Strecke sehen Sie unter Berichte → Fahrer-Tracking →
            Phase „GPS-Auffälligkeiten“.
          </Typography>

          {gpsErkennungAktiv && (
            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={gpsExempt}
                    onChange={(event) => dispatch(setDriverGpsExempt({
                      driverId: id,
                      exempt: event.target.checked
                    }))}
                  />
                }
                label="Von der GPS-Erkennung ausnehmen"
              />
              <Typography variant="caption" color="textSecondary" component="div">
                Für Test- und Entwicklungskonten sowie die Prüfkonten von Google Play und dem
                App Store. Diese laufen auf Emulatoren und melden dauerhaft einen simulierten Standort.
              </Typography>
            </Box>
          )}

          {gpsEventsLoading && (
            <Typography variant="body2" color="textSecondary">Wird geladen…</Typography>
          )}
          {!gpsEventsLoading && gpsEvents.length === 0 && (
            <Typography variant="body2" color="textSecondary">
              {gpsExempt
                ? 'Dieser Fahrer ist von der Erkennung ausgenommen.'
                : 'Keine Einträge im Aufbewahrungszeitraum.'}
            </Typography>
          )}

          {!gpsEventsLoading && gpsEvents.map(event => {
            const isMock = event.type === 'mock_provider';
            const isTime = event.type === 'time_anomaly';
            const title = isMock
              ? 'Standort-Simulation aktiv (Mock-Provider)'
              : isTime
                ? 'Zeitstempel aus der Zukunft'
                : 'Unmöglicher Standortsprung';
            const activeFlags = Object.entries(event.flags || {})
              .filter(([, value]) => value === true)
              .map(([key]) => ({
                speedZeroWhileMoving: 'Bewegung ohne Geschwindigkeit',
                constantAccuracy: 'konstante Genauigkeit',
                noHeading: 'keine Fahrtrichtung'
              }[key] || key));
            return (
              <Card key={event.id} variant="outlined" sx={{ mb: 1 }}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 0.5 }}>
                    <Typography variant="subtitle2">{title}</Typography>
                    <Chip
                      size="small"
                      label={event.action === 'blocked' ? 'Sperre ausgelöst' : 'nur protokolliert'}
                      color={event.action === 'blocked' ? 'error' : 'default'}
                    />
                    <Chip
                      size="small"
                      variant="outlined"
                      label={event.detectedBy === 'server' ? 'Serverprüfung' : 'App-Erkennung'}
                    />
                    {event.sectorName && (
                      <Chip size="small" variant="outlined" label={`Sektor: ${event.sectorName}`} />
                    )}
                  </Box>
                  <Typography variant="body2" color="textSecondary">
                    {event.createdAtMs ? new Date(event.createdAtMs).toLocaleString('de-DE') : 'Zeit unbekannt'}
                    {event.distanceMeters != null && (
                      ` — ${Math.round(event.distanceMeters).toLocaleString('de-DE')} m in ${Math.round(event.dtSeconds || 0)} s` +
                      ` (${Math.round(event.impliedKmh || 0).toLocaleString('de-DE')} km/h)`
                    )}
                  </Typography>
                  {event.to && (
                    <Typography variant="caption" color="textSecondary" component="div">
                      Zielposition: {event.to.lat?.toFixed(5)}, {event.to.lng?.toFixed(5)}
                      {event.from && ` · von ${event.from.lat?.toFixed(5)}, ${event.from.lng?.toFixed(5)}`}
                    </Typography>
                  )}
                  {activeFlags.length > 0 && (
                    <Typography variant="caption" color="textSecondary" component="div">
                      Weitere Hinweise: {activeFlags.join(', ')}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Paper>
      )}

      {/* KPIs/Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <DetailCard elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LicenseIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">P-Schein</Typography>
              </Box>
              <Typography variant="body1">{currentDriver.license_number || 'Nicht angegeben'}</Typography>
              <Typography variant="body2" color="textSecondary">
                Fahrernummer: {currentDriver.driverNumber || 'Nicht vergeben'}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="body2" color="textSecondary">
                  Gültig bis: {formatDate(currentDriver.license_expiry)}
                </Typography>
                {(() => {
                  const lic = getLicenseExpiryStatus(currentDriver.license_expiry);
                  return lic ? (
                    <Chip
                      label={lic.label}
                      size="small"
                      icon={<LicenseExpiryIcon />}
                      color={lic.color}
                      variant="outlined"
                      sx={{ height: 22, fontSize: '0.7rem' }}
                    />
                  ) : null;
                })()}
              </Box>
            </CardContent>
          </DetailCard>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <DetailCard elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CarIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Fahrzeug</Typography>
              </Box>
              <Typography variant="body1">
                {currentDriver.vehicle ? 
                  `${currentDriver.vehicle.model} (${currentDriver.vehicle.license_plate})` : 
                  'Kein Fahrzeug zugewiesen'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Status: {currentDriver.vehicle ? translateStatus(currentDriver.vehicle.status) : 'N/A'}
              </Typography>
            </CardContent>
          </DetailCard>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <DetailCard elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TaxiIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Fahrten</Typography>
              </Box>
              <Typography variant="h4">{currentDriver.total_rides || 0}</Typography>
              <Typography variant="body2" color="textSecondary">
                Abgeschlossene Fahrten insgesamt
              </Typography>
            </CardContent>
          </DetailCard>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <DetailCard elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TimerIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Letzte Aktivität</Typography>
              </Box>
              <Typography variant="body1">
                {formatDateTime(currentDriver.last_active)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Registriert seit: {formatDate(currentDriver.created_at)}
              </Typography>
            </CardContent>
          </DetailCard>
        </Grid>
      </Grid>
      
      {/* Tabs für Fahrten und Schichten */}
      <Paper sx={{ width: '100%', mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label="Fahrten" />
          <Tab label="Schichten" />
          <Tab label="Dokumente" />
          <Tab label={`Bewertungen (${(currentDriver.ratings || []).length})`} />
        </Tabs>
        
        {/* Fahrten Tab */}
        <TabPanel value={tabValue} index={0}>
          <Typography variant="h6" gutterBottom>
            Letzte Fahrten
          </Typography>
          <List>
            {getDriverRides().length > 0 ? (
              getDriverRides().slice(0, 5).map((ride) => (
                <Paper key={ride.id} sx={{ mb: 2, p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle1">
                      {ride.pickup_address?.substring(0, 30)}... → {ride.destination_address?.substring(0, 30)}...
                    </Typography>
                    <RideStatusChip
                      label={getRideStatusLabel(ride)}
                      status={ride.status}
                      size="small"
                    />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="textSecondary">
                      Datum: {formatDateTime(ride.request_time)}
                    </Typography>
                    <Typography variant="body2">
                      Fahrpreis: {ride.fare ? `${ride.fare.toFixed(2)} €` : 'N/A'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                    <Button 
                      variant="outlined" 
                      size="small"
                      component={Link}
                      to={`/fahrten/${ride.id}`}
                    >
                      Details anzeigen
                    </Button>
                  </Box>
                </Paper>
              ))
            ) : (
              <Typography variant="body1" align="center" sx={{ py: 3 }}>
                Keine Fahrten für diesen Fahrer gefunden.
              </Typography>
            )}
          </List>
          {getDriverRides().length > 5 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button 
                variant="contained" 
                component={Link} 
                to={`/fahrten?driverId=${id}`}
              >
                Alle Fahrten anzeigen
              </Button>
            </Box>
          )}
        </TabPanel>
        
        {/* Schichten Tab */}
        <TabPanel value={tabValue} index={1}>
          <Typography variant="h6" gutterBottom>
            Letzte Schichten
          </Typography>
          <List>
            {getDriverShifts().length > 0 ? (
              getDriverShifts().slice(0, 5).map((shift) => (
                <Paper key={shift.id} sx={{ mb: 2, p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle1">
                      {formatDate(shift.start_time)} ({formatDateTime(shift.start_time)} - {formatDateTime(shift.end_time)})
                    </Typography>
                    <Chip
                      label={translateStatus(shift.status)}
                      color={shift.status === 'completed' ? 'success' : 
                             shift.status === 'active' ? 'primary' : 
                             shift.status === 'scheduled' ? 'info' : 'default'}
                      size="small"
                    />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="textSecondary">
                      Fahrzeug: {shift.vehicle ? shift.vehicle.model : 'N/A'}
                    </Typography>
                    <Typography variant="body2">
                      Fahrten: {shift.total_rides || 0} | Einnahmen: {shift.total_earnings ? `${shift.total_earnings.toFixed(2)} €` : '0.00 €'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                    <Button 
                      variant="outlined" 
                      size="small"
                      component={Link}
                      to={`/schichten/${shift.id}`}
                    >
                      Details anzeigen
                    </Button>
                  </Box>
                </Paper>
              ))
            ) : (
              <Typography variant="body1" align="center" sx={{ py: 3 }}>
                Keine Schichten für diesen Fahrer gefunden.
              </Typography>
            )}
          </List>
          {getDriverShifts().length > 5 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button 
                variant="contained" 
                component={Link} 
                to={`/schichten?driverId=${id}`}
              >
                Alle Schichten anzeigen
              </Button>
            </Box>
          )}
        </TabPanel>
        
        {/* Dokumente Tab */}
        <TabPanel value={tabValue} index={2}>
          <Typography variant="h6" gutterBottom>
            Dokumente
          </Typography>
          <List>
            {currentDriver.documents && currentDriver.documents.length > 0 ? (
              currentDriver.documents.map((doc, index) => (
                <Paper key={index} sx={{ mb: 2, p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle1">
                      Dokument {index + 1}
                    </Typography>
                    <Box>
                      <IconButton 
                        size="small" 
                        onClick={() => navigator.clipboard.writeText(doc)}
                        title="URL kopieren"
                      >
                        <CopyIcon fontSize="small" />
                      </IconButton>
                      <Button 
                        variant="outlined" 
                        size="small"
                        href={doc}
                        target="_blank"
                        sx={{ ml: 1 }}
                      >
                        Öffnen
                      </Button>
                    </Box>
                  </Box>
                </Paper>
              ))
            ) : (
              <Typography variant="body1" align="center" sx={{ py: 3 }}>
                Keine Dokumente für diesen Fahrer hinterlegt.
              </Typography>
            )}
          </List>
        </TabPanel>

        {/* Bewertungen Tab */}
        <TabPanel value={tabValue} index={3}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="h6">
              Kundenbewertungen
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Rating value={currentDriver.rating || 0} precision={0.5} readOnly size="small" />
              <Typography variant="body2" color="textSecondary">
                Ø {currentDriver.rating || '0'}/5 ({currentDriver.ratingCount || 0} Bewertungen)
              </Typography>
            </Box>
          </Box>
          <List>
            {getSortedRatings().length > 0 ? (
              getSortedRatings().map((rating) => (
                <Paper key={rating.id} sx={{ mb: 2, p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Rating value={rating.rating || 0} precision={0.5} readOnly size="small" />
                        <Typography variant="body2" color="textSecondary">
                          {formatRatingDate(rating.timestamp)}
                        </Typography>
                      </Box>
                      <Typography variant="body1" sx={{ mt: 1, overflowWrap: 'anywhere' }}>
                        {rating.comment ? rating.comment : <em>Kein Kommentar</em>}
                      </Typography>
                      {rating.rideId && (
                        <Button
                          variant="text"
                          size="small"
                          component={Link}
                          to={`/fahrten/${rating.rideId}`}
                          sx={{ mt: 0.5, px: 0 }}
                        >
                          Zur Fahrt
                        </Button>
                      )}
                    </Box>
                    {canManageRatings && (
                      <Box sx={{ display: 'flex', flexShrink: 0 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleRatingEditOpen(rating)}
                          title="Bewertung bearbeiten"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRatingDeleteOpen(rating)}
                          title="Bewertung löschen"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                </Paper>
              ))
            ) : (
              <Typography variant="body1" align="center" sx={{ py: 3 }}>
                Keine Bewertungen für diesen Fahrer vorhanden.
              </Typography>
            )}
          </List>
        </TabPanel>
      </Paper>

      {/* Löschen-Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        title="Fahrer löschen"
        content={`Möchten Sie den Fahrer ${currentDriver.user?.name || ''} wirklich löschen? Dieser Vorgang kann nicht rückgängig gemacht werden.`}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
      
      {/* Fahrer abmelden Dialog */}
      <Dialog open={logoutDialogOpen} onClose={handleLogoutCancel} maxWidth="sm">
        <DialogTitle>Fahrer abmelden</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body1" gutterBottom>
              Möchten Sie den Fahrer {currentDriver?.user?.name} wirklich abmelden?
            </Typography>
            {currentDriver?.vehicle && (
              <Typography variant="body2" color="error">
                Hinweis: Der Fahrer wird vom Fahrzeug {currentDriver.vehicle.model} ({currentDriver.vehicle.licensePlate}) abgemeldet. Die mobile App wird den Fahrer automatisch abmelden.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLogoutCancel}>Abbrechen</Button>
          <Button 
            onClick={handleLogoutConfirm} 
            variant="contained" 
            color="primary"
          >
            Fahrer abmelden
          </Button>
        </DialogActions>
      </Dialog>

      {/* Passwort-zurücksetzen-Dialog */}
      <Dialog open={passwordDialogOpen} onClose={handlePasswordDialogClose} maxWidth="xs" fullWidth>
        <DialogTitle>Passwort zurücksetzen</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Neues Login-Passwort für {currentDriver?.user?.name || 'den Fahrer'}
            {currentDriver?.user?.email ? ` (${currentDriver.user.email})` : ''} festlegen.
            Das Passwort wird nicht gespeichert – bitte dem Fahrer mitteilen.
          </DialogContentText>
          <TextField
            label="Neues Passwort"
            type="text"
            fullWidth
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            sx={{ mb: 2 }}
            disabled={passwordSubmitting}
          />
          <TextField
            label="Passwort bestätigen"
            type="text"
            fullWidth
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={passwordSubmitting}
          />
          {passwordError && (
            <Alert severity="error" sx={{ mt: 2 }}>{passwordError}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePasswordDialogClose} disabled={passwordSubmitting}>Abbrechen</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handlePasswordResetConfirm}
            disabled={passwordSubmitting}
          >
            {passwordSubmitting ? 'Wird gesetzt…' : 'Passwort setzen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Verfügbarkeits-Dialog */}
      <Dialog open={availabilityDialogOpen} onClose={handleAvailabilityCancel} maxWidth="sm">
        {currentDriver?.isAvailable ? (
          <>
            <DialogTitle>Fahrer besetzt setzen</DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ mb: 2 }}>
                Pausenart für {currentDriver?.user?.name} auswählen:
              </DialogContentText>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<TimerIcon />}
                  onClick={() => handlePauseSelect('Stadtfahrt', 15)}
                  fullWidth
                >
                  Stadtfahrt ca. 15 min
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<TimerIcon />}
                  onClick={() => handlePauseSelect('Pause', 30)}
                  fullWidth
                >
                  Pause ca. 30 min
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<TimerIcon />}
                  onClick={() => handlePauseSelect('Langstrecke', 45)}
                  fullWidth
                >
                  Langstrecke ca. 45 min
                </Button>
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<PauseIcon />}
                  onClick={() => handlePauseSelect('Disposition', null)}
                  fullWidth
                >
                  Disposition (unbefristet)
                </Button>
              </Box>
              <DialogContentText sx={{ mt: 2, fontSize: '0.85rem', color: 'text.secondary' }}>
                Die mobile App wird den Status automatisch aktualisieren.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleAvailabilityCancel}>Abbrechen</Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogTitle>Fahrer verfügbar setzen</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Möchten Sie {currentDriver?.user?.name} wieder als verfügbar markieren?
                Die mobile App wird den Status automatisch aktualisieren.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleAvailabilityCancel}>Abbrechen</Button>
              <Button
                onClick={handleAvailableConfirm}
                variant="contained"
                color="success"
              >
                Verfügbar setzen
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Bewertung-bearbeiten-Dialog */}
      <Dialog open={ratingEditOpen} onClose={handleRatingEditClose} maxWidth="xs" fullWidth>
        <DialogTitle>Bewertung bearbeiten</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Sterne
            </Typography>
            <Rating
              value={ratingEditValue}
              onChange={(event, value) => setRatingEditValue(value)}
              precision={1}
              size="large"
              disabled={ratingSubmitting}
            />
            <TextField
              label="Kommentar"
              fullWidth
              multiline
              minRows={2}
              value={ratingEditComment}
              onChange={(e) => setRatingEditComment(e.target.value)}
              disabled={ratingSubmitting}
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRatingEditClose} disabled={ratingSubmitting}>Abbrechen</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleRatingEditSave}
            disabled={ratingSubmitting || !ratingEditValue}
          >
            {ratingSubmitting ? 'Wird gespeichert…' : 'Speichern'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bewertung-löschen-Dialog */}
      <DeleteConfirmDialog
        open={ratingDeleteOpen}
        title="Bewertung löschen"
        content="Möchten Sie diese Bewertung wirklich löschen? Die Durchschnittsbewertung des Fahrers wird neu berechnet. Dieser Vorgang kann nicht rückgängig gemacht werden."
        onConfirm={handleRatingDeleteConfirm}
        onCancel={handleRatingDeleteCancel}
      />

      {/* Snackbar für Benachrichtigungen */}
      <Snackbar
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DriverDetails;
